/**
 * OrganizationThemeSelector Component
 *
 * A comprehensive filter component with drilldown and tab change functionalities for Audit Overview.
 *
 * Features:
 * 1. **Tab Change Functionality:**
 *    - Toggle between 'Organization' and 'Theme' views
 *    - Visual indicator shows active tab
 *    - Bidirectional arrow icon for easy switching
 *    - Resets selections when switching modes
 *
 * 2. **Drilldown Functionality:**
 *    - Dynamic dropdown that changes based on selected view mode
 *    - Search functionality to filter organizations/themes
 *    - Click outside to close dropdown
 *    - Selected item displayed in dropdown header
 *    - Callback functions trigger data refresh on selection
 *
 * 3. **Time Period Selection:**
 *    - Three options: 1 Year, 2 Year, Custom
 *    - Visual active state for selected period
 *    - Custom date range support (can be extended with date picker)
 *
 * 4. **Callback Functions:**
 *    - onViewModeChange: Triggered when switching between Organization/Theme
 *    - onOrganizationChange: Triggered when organization is selected
 *    - onThemeChange: Triggered when theme is selected
 *    - onTimePeriodChange: Triggered when time period changes
 *    - onCustomDateChange: For custom date range selection
 *
 * Usage Example:
 * ```tsx
 * <OrganizationThemeSelector
 *   onViewModeChange={(mode) => setViewMode(mode)}
 *   onOrganizationChange={(id) => fetchOrganizationData(id)}
 *   onThemeChange={(id) => fetchThemeData(id)}
 *   onTimePeriodChange={(period) => updateTimePeriod(period)}
 * />
 * ```
 */

import React, { useState, useRef, useEffect } from "react";
import styles from './styles.module.scss';
import DateRangePicker from '../DateRangePicker';

type ViewMode = 'organization' | 'theme';
type TimePeriod = '1year' | '2year' | 'custom';

interface Organization {
    id: string;
    name: string;
}

interface Theme {
    id: string;
    name: string;
}

interface OrganizationThemeSelectorProps {
    organizations?: Organization[];
    themes?: Theme[];
    onViewModeChange?: (mode: ViewMode) => void;
    onOrganizationChange?: (organizationId: string) => void;
    onThemeChange?: (themeId: string) => void;
    onTimePeriodChange?: (period: TimePeriod) => void;
    onCustomDateChange?: (startDate: Date, endDate: Date) => void;
}

const OrganizationThemeSelector: React.FC<OrganizationThemeSelectorProps> = ({
    organizations: organizationsProp = [],
    themes: themesProp = [],
    onViewModeChange,
    onOrganizationChange,
    onThemeChange,
    onTimePeriodChange,
    onCustomDateChange
}) => {
    // State management
    const [viewMode, setViewMode] = useState<ViewMode>('organization');
    const [timePeriod, setTimePeriod] = useState<TimePeriod>('1year');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedOrganization, setSelectedOrganization] = useState<string>('');
    const [selectedTheme, setSelectedTheme] = useState<string>('');
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const [customDateRange, setCustomDateRange] = useState<{ start: Date | null; end: Date | null }>({ start: null, end: null });

    const dropdownRef = useRef<HTMLDivElement>(null);
    const customButtonRef = useRef<HTMLButtonElement>(null);

    // Map API response to match Organization/Theme interface
    const mapOrganizations = (orgs: any[]): Organization[] => {
        return orgs.map(org => ({
            id: org.organizationName || org.name,  // Use name as ID
            name: org.organizationName || org.name
        }));
    };

    const mapThemes = (thms: any[]): Theme[] => {
        return thms.map(thm => ({
            id: thm.themeName || thm.name,  // Use name as ID
            name: thm.themeName || thm.name
        }));
    };

    // Use props if provided, otherwise fall back to sample data
    const organizations: Organization[] = mapOrganizations(organizationsProp);

    const themes: Theme[] = mapThemes(themesProp)

    // Set initial selection when organizations/themes are loaded
    useEffect(() => {
        if (viewMode === 'organization' && organizations.length > 0 && !selectedOrganization) {
            const firstOrg = organizations[0].id;
            setSelectedOrganization(firstOrg);
            if (onOrganizationChange) {
                onOrganizationChange(firstOrg);
            }
        }
    }, [organizations, viewMode, selectedOrganization, onOrganizationChange]);

    useEffect(() => {
        if (viewMode === 'theme' && themes.length > 0 && !selectedTheme) {
            const firstTheme = themes[0].id;
            setSelectedTheme(firstTheme);
            if (onThemeChange) {
                onThemeChange(firstTheme);
            }
        }
    }, [themes, viewMode, selectedTheme, onThemeChange]);

    // Get current list based on view mode
    const getCurrentList = () => {
        return viewMode === 'organization' ? organizations : themes;
    };

    // Filter list based on search query
    const getFilteredList = () => {
        const list = getCurrentList();
        if (!searchQuery) return list;
        return list.filter(item =>
            item.name.toLowerCase().includes(searchQuery.toLowerCase())
        );
    };

    // Handle view mode toggle (Organization <-> Theme)
    const handleViewModeToggle = () => {
        const newMode: ViewMode = viewMode === 'organization' ? 'theme' : 'organization';
        setViewMode(newMode);
        setSearchQuery('');
        setIsDropdownOpen(false);

        // Reset selections when switching modes
        if (newMode === 'organization') {
            setSelectedTheme('');
        } else {
            setSelectedOrganization('');
        }

        // Notify parent component
        if (onViewModeChange) {
            onViewModeChange(newMode);
        }
    };

    // Handle item selection from dropdown
    const handleItemSelect = (itemId: string) => {
        if (viewMode === 'organization') {
            setSelectedOrganization(itemId);
            if (onOrganizationChange) {
                onOrganizationChange(itemId);
            }
        } else {
            setSelectedTheme(itemId);
            if (onThemeChange) {
                onThemeChange(itemId);
            }
        }
        setIsDropdownOpen(false);
        setSearchQuery('');
    };

    // Handle time period change
    const handleTimePeriodChange = (period: TimePeriod) => {
        setTimePeriod(period);

        // If custom is selected, open date picker
        if (period === 'custom') {
            setIsDatePickerOpen(true);
        } else {
            // For 1year and 2year, trigger callback with period
            if (onTimePeriodChange) {
                onTimePeriodChange(period);
            }
        }
    };

    // Handle custom date range selection
    const handleCustomDateSelect = (start: Date, end: Date) => {
        setCustomDateRange({ start, end });
        setIsDatePickerOpen(false);

        // Trigger callback with custom dates
        if (onCustomDateChange) {
            onCustomDateChange(start, end);
        }
    };

    // Handle date picker close without selection
    const handleDatePickerClose = () => {
        setIsDatePickerOpen(false);
        // Reset to previous selection if custom dates weren't selected
        if (!customDateRange.start || !customDateRange.end) {
            setTimePeriod('1year');
        }
    };

    // Get selected item name for display
    const getSelectedItemName = () => {
        const currentId = viewMode === 'organization' ? selectedOrganization : selectedTheme;
        if (!currentId) {
            return `Select ${viewMode === 'organization' ? 'Organization' : 'Theme'}`;
        }
        const item = getCurrentList().find(item => item.id === currentId);
        return item ? item.name : `Select ${viewMode === 'organization' ? 'Organization' : 'Theme'}`;
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div className={styles.filterContainer}>
            {/* Left Section */}
            <div className={styles.leftSection}>
                {/* Toggle Header */}
                <div className={styles.toggleWrapper}>
                    <div className={styles.toggle}>
                        <div
                            className={viewMode === 'organization' ? styles.toggleActive : styles.toggleInactive}
                            onClick={viewMode === 'theme' ? handleViewModeToggle : undefined}
                            onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && viewMode === 'theme') handleViewModeToggle(); }}
                            role="button"
                            tabIndex={viewMode === 'theme' ? 0 : -1}
                            style={{ cursor: viewMode === 'theme' ? 'pointer' : 'default' }}
                        >
                            Organization
                        </div>
                        <div
                            className={styles.toggleIcon}
                            onClick={handleViewModeToggle}
                            onKeyDown={(e) => {
                                             if (e.key === "Enter" || e.key === " ") {
                                                 handleViewModeToggle();
                                                }
                                              }}
                                             role="button"
                                             tabIndex={0}
                            style={{ cursor: 'pointer' }}
                        >
                            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M6 2.25L3 5.25L6 8.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M3 5.25H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M12 15.75L15 12.75L12 9.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M15 12.75H3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <div
                            className={viewMode === 'theme' ? styles.toggleActive : styles.toggleInactive}
                            onClick={viewMode === 'organization' ? handleViewModeToggle : undefined}
                            onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && viewMode === 'organization') handleViewModeToggle(); }}
                            role="button"
                            tabIndex={viewMode === 'organization' ? 0 : -1}
                            style={{ cursor: viewMode === 'organization' ? 'pointer' : 'default' }}
                        >
                            Theme
                        </div>
                    </div>
                </div>

                {/* Dropdown */}
                <div className={styles.dropdownWrapper} ref={dropdownRef}>
                    <div
                        className={styles.dropdownHeader}
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        onKeyDown={(e) => {
                                             if (e.key === "Enter" || e.key === " ") {
                                                 setIsDropdownOpen(!isDropdownOpen);
                                                }
                                              }}
                                             role="button"
                                             tabIndex={0}
                    >
                        <span className={styles.dropdownLabel}>
                            <div>
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M6.66602 8H9.33268" stroke="#1A2353" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M6.66602 5.33325H9.33268" stroke="#1A2353" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M9.33268 14.0001V12.0001C9.33268 11.6465 9.19221 11.3073 8.94216 11.0573C8.69211 10.8072 8.35297 10.6667 7.99935 10.6667C7.64573 10.6667 7.30659 10.8072 7.05654 11.0573C6.80649 11.3073 6.66602 11.6465 6.66602 12.0001V14.0001" stroke="#1A2353" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M4.00065 6.66675H2.66732C2.3137 6.66675 1.97456 6.80722 1.72451 7.05727C1.47446 7.30732 1.33398 7.64646 1.33398 8.00008V12.6667C1.33398 13.0204 1.47446 13.3595 1.72451 13.6096C1.97456 13.8596 2.3137 14.0001 2.66732 14.0001H13.334C13.6876 14.0001 14.0267 13.8596 14.2768 13.6096C14.5268 13.3595 14.6673 13.0204 14.6673 12.6667V6.00008C14.6673 5.64646 14.5268 5.30732 14.2768 5.05727C14.0267 4.80722 13.6876 4.66675 13.334 4.66675H12.0007" stroke="#1A2353" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M4 14V3.33333C4 2.97971 4.14048 2.64057 4.39052 2.39052C4.64057 2.14048 4.97971 2 5.33333 2H10.6667C11.0203 2 11.3594 2.14048 11.6095 2.39052C11.8595 2.64057 12 2.97971 12 3.33333V14" stroke="#1A2353" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            &nbsp;&nbsp;{getSelectedItemName()}
                        </span>
                        <span className={`${styles.chevron} ${isDropdownOpen ? styles.chevronUp : ''}`}>
                            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M4.5 6.75L9 11.25L13.5 6.75" stroke="#99A1AF" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                        </span>
                    </div>

                    {isDropdownOpen && (
                        <div className={styles.dropdownContent}>
                            <div className={styles.searchBox}>
                                <span className={styles.searchIcon} />
                                <input
                                    className={styles.searchInput}
                                    placeholder={`Search ${viewMode === 'organization' ? 'Organization' : 'Theme'}`}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    autoFocus
                                />
                            </div>

                            <div className={styles.list}>
                                {getFilteredList().length === 0 ? (
                                    <div className={styles.noResults}>
                                        No {viewMode === 'organization' ? 'organizations' : 'themes'} found
                                    </div>
                                ) : (
                                    getFilteredList().map(item => {
                                        const isSelected = viewMode === 'organization'
                                            ? selectedOrganization === item.id
                                            : selectedTheme === item.id;

                                        return (
                                            <div
                                                key={item.id}
                                                className={`${styles.listItem} ${isSelected ? styles.active : ''}`}
                                                onClick={() => handleItemSelect(item.id)}
                                               onKeyDown={(e) => {
                                             if (e.key === "Enter" || e.key === " ") {
                                                 handleItemSelect(item.id);
                                                }
                                              }}
                                             role="button"
                                             tabIndex={0}
                                            >
                                                <span>{item.name}</span>
                                                {isSelected && <span className={styles.radio} />}
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Right Section */}
            <div className={styles.rightSection}>
                <button
                    className={timePeriod === '1year' ? styles.primaryBtn : styles.outlineBtn}
                    onClick={() => handleTimePeriodChange('1year')}
                >
                    1 Year
                </button>
                <button
                    className={timePeriod === '2year' ? styles.primaryBtn : styles.outlineBtn}
                    onClick={() => handleTimePeriodChange('2year')}
                >
                    2 Years
                </button>
                <div style={{ position: 'relative' }}>
                    <button
                        ref={customButtonRef}
                        className={timePeriod === 'custom' ? styles.primaryBtn : styles.outlineBtn}
                        onClick={() => handleTimePeriodChange('custom')}
                    >
                        <span className={styles.calendarIcon}>
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <g clipPath="url(#clip0_969_1526)">
                                    <path d="M4.66602 1.16675V3.50008" stroke="currentColor" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M9.33398 1.16675V3.50008" stroke="currentColor" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M11.0833 2.33325H2.91667C2.27233 2.33325 1.75 2.85559 1.75 3.49992V11.6666C1.75 12.3109 2.27233 12.8333 2.91667 12.8333H11.0833C11.7277 12.8333 12.25 12.3109 12.25 11.6666V3.49992C12.25 2.85559 11.7277 2.33325 11.0833 2.33325Z" stroke="currentColor" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M1.75 5.83325H12.25" stroke="currentColor" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                                </g>
                                <defs>
                                    <clipPath id="clip0_969_1526">
                                        <rect width="14" height="14" fill="white" />
                                    </clipPath>
                                </defs>
                            </svg>
                        </span>
                        Custom
                    </button>

                    {/* Date Range Picker Dropdown */}
                    <DateRangePicker
                        isOpen={isDatePickerOpen}
                        onClose={handleDatePickerClose}
                        onSelect={handleCustomDateSelect}
                        anchorRef={customButtonRef}
                    />
                </div>
            </div>
        </div>
    );
};

export default OrganizationThemeSelector;
